import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PanoramaAssembler from './PanoramaAssembler';

const baseURL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://satoshi-homepage-59650ad0a3ce.herokuapp.com';

// Shared card + table styling for the admin tabs
const cardStyle = {
  backgroundColor: '#283593',
  border: '1px solid rgba(0, 191, 255, 0.4)',
  color: '#FFFFFF',
  padding: '1.5rem',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
  marginBottom: '1.5rem'
};

// Retro accent: section headings use the arcade font
const sectionTitleStyle = {
  margin: '0 0 1rem',
  color: '#FFFFFF',
  fontSize: '0.85rem',
  fontFamily: "'Press Start 2P', 'Courier New', monospace",
  letterSpacing: '1px',
  textTransform: 'uppercase'
};

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: '2px solid rgba(255, 255, 255, 0.3)',
  color: '#9dd5ff',
  fontWeight: 600,
  whiteSpace: 'nowrap'
};
const tdStyle = { padding: '8px 10px', borderBottom: '1px solid rgba(255, 255, 255, 0.12)', color: '#FFFFFF' };

const fmtUsd = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtNum = (n) => (Number(n) || 0).toLocaleString();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const StatTile = ({ label, value, accent }) => (
  <div style={{
    flex: '1 1 140px',
    minWidth: '140px',
    backgroundColor: '#1a237e',
    border: '1px solid rgba(0, 191, 255, 0.4)',
    borderRadius: '10px',
    padding: '14px 16px'
  }}>
    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: accent || '#00E5FF', fontFamily: "'Press Start 2P', 'Courier New', monospace", lineHeight: 1.4 }}>
      {value}
    </div>
    <div style={{ fontSize: '0.75rem', color: '#9dd5ff', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
  </div>
);

const TileRow = ({ children }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '1rem' }}>
    {children}
  </div>
);

// 30-day signup mini bar chart, plain divs — no chart library
const SignupSparkline = ({ series }) => {
  if (!series || series.length === 0) {
    return <p style={{ color: '#9dd5ff', fontSize: '0.85rem' }}>No signups in the last 30 days.</p>;
  }
  const max = Math.max(...series.map((d) => d.count), 1);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '90px' }}>
        {series.map((d) => (
          <div
            key={d.day}
            title={`${d.day}: ${d.count} signup${d.count === 1 ? '' : 's'}`}
            style={{
              flex: 1,
              minWidth: '4px',
              height: `${Math.max(6, (d.count / max) * 90)}px`,
              backgroundColor: '#00BFFF',
              borderRadius: '3px 3px 0 0'
            }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#9dd5ff', marginTop: '4px' }}>
        <span>{series[0].day}</span>
        <span>{series[series.length - 1].day}</span>
      </div>
    </div>
  );
};

const AnalyticsTab = ({ adminFetch }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await adminFetch('/api/admin/analytics');
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminFetch]);

  if (loading) return <div style={cardStyle}>Loading analytics…</div>;
  if (error) return <div style={{ ...cardStyle, color: '#fca5a5' }}>Failed to load analytics: {error}</div>;
  if (!data) return null;

  // Defensive defaults: a partial payload degrades to zeros instead of
  // blanking the whole panel
  const users = data.users || {};
  const loans = data.loans || {};
  const referrals = data.referrals || {};
  const dogCards = data.dogCards || {};
  const revenue = {
    bitWheel: { usd: 0, recent: [], ...(data.revenue?.bitWheel || {}) },
    bitFlip: { usd: 0, recent: [], ...(data.revenue?.bitFlip || {}) },
    orders: { byApp: [], recent: [], ...(data.revenue?.orders || {}) },
    ...(data.revenue ? {} : {})
  };
  const totalRevenue =
    (revenue.bitWheel.usd || 0) +
    (revenue.bitFlip.usd || 0) +
    (revenue.orders.byApp || []).reduce((sum, r) => sum + (r.usd || 0), 0);

  return (
    <div>
      {/* Users & signups */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>👥 Users &amp; Signups</h2>
        <TileRow>
          <StatTile label="Total users" value={fmtNum(users.total)} />
          <StatTile label="Verified" value={fmtNum(users.verified)} />
          <StatTile label="New (7 days)" value={fmtNum(users.newLast7)} accent="#10B981" />
          <StatTile label="New (30 days)" value={fmtNum(users.newLast30)} accent="#10B981" />
        </TileRow>
        <h3 style={{ ...sectionTitleStyle, fontSize: '0.9rem' }}>Signups per day (last 30 days)</h3>
        <SignupSparkline series={users.signupsPerDay} />
        <h3 style={{ ...sectionTitleStyle, fontSize: '0.9rem', marginTop: '1.25rem' }}>Signups by source</h3>
        <table style={tableStyle}>
          <thead>
            <tr><th style={thStyle}>Source</th><th style={thStyle}>Users</th></tr>
          </thead>
          <tbody>
            {(users.bySource || []).map((r) => (
              <tr key={r.source}>
                <td style={tdStyle}>{r.source}</td>
                <td style={tdStyle}>{fmtNum(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Activity */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>⚡ Activity</h2>
        <TileRow>
          <StatTile label="Active (24h)" value={fmtNum(users.activeLast24h)} accent="#10B981" />
          <StatTile label="Active (7 days)" value={fmtNum(users.activeLast7d)} />
          <StatTile label="Lifetime logins" value={fmtNum(users.totalLogins)} />
        </TileRow>
      </div>

      {/* Revenue */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>💰 Revenue</h2>
        <TileRow>
          <StatTile label="Tracked total" value={fmtUsd(totalRevenue)} accent="#10B981" />
          <StatTile label={`Wheel (${revenue.bitWheel.purchases})`} value={fmtUsd(revenue.bitWheel.usd)} />
          <StatTile label={`Flip (${revenue.bitFlip.purchases})`} value={fmtUsd(revenue.bitFlip.usd)} />
        </TileRow>
        {revenue.orders.byApp.length > 0 && (
          <>
            <h3 style={{ ...sectionTitleStyle, fontSize: '0.9rem' }}>Orders by app</h3>
            <table style={tableStyle}>
              <thead>
                <tr><th style={thStyle}>App</th><th style={thStyle}>Orders</th><th style={thStyle}>USD</th></tr>
              </thead>
              <tbody>
                {revenue.orders.byApp.map((r) => (
                  <tr key={r.app}>
                    <td style={tdStyle}>{r.app}</td>
                    <td style={tdStyle}>{r.count}</td>
                    <td style={tdStyle}>{fmtUsd(r.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {(revenue.subscriptions || []).length > 0 && (
          <>
            <h3 style={{ ...sectionTitleStyle, fontSize: '0.9rem', marginTop: '1.25rem' }}>Active subscriptions</h3>
            <table style={tableStyle}>
              <thead>
                <tr><th style={thStyle}>Level</th><th style={thStyle}>Users</th><th style={thStyle}>USD</th></tr>
              </thead>
              <tbody>
                {(revenue.subscriptions || []).map((r) => (
                  <tr key={r.level}>
                    <td style={tdStyle}>{r.level}</td>
                    <td style={tdStyle}>{r.count}</td>
                    <td style={tdStyle}>{fmtUsd(r.usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        <h3 style={{ ...sectionTitleStyle, fontSize: '0.9rem', marginTop: '1.25rem' }}>Recent purchases</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>What</th>
                <th style={thStyle}>USD</th>
                <th style={thStyle}>Crypto</th>
              </tr>
            </thead>
            <tbody>
              {[
                ...revenue.bitWheel.recent.map((r) => ({ ...r, what: `${r.spins || '?'} wheel spins` })),
                ...revenue.bitFlip.recent.map((r) => ({ ...r, what: `${r.flips || '?'} flips` })),
                ...revenue.orders.recent.map((r) => ({ ...r, what: r.description || r.app, crypto: r.cryptoCurrency }))
              ]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 12)
                .map((r, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{fmtDate(r.createdAt)}</td>
                    <td style={tdStyle}>{r.email}</td>
                    <td style={tdStyle}>{r.what}</td>
                    <td style={tdStyle}>{fmtUsd(r.usdAmount)}</td>
                    <td style={tdStyle}>{r.crypto || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Loan applications */}
      <div style={cardStyle}>
        <h2 style={sectionTitleStyle}>🏦 Loan &amp; Lend Applications</h2>
        <TileRow>
          {(loans.counts || []).length === 0 && <StatTile label="Applications" value="0" />}
          {(loans.counts || []).map((r) => (
            <StatTile
              key={`${r.type}-${r.status}`}
              label={`${r.type} · ${r.status}`}
              value={fmtNum(r.count)}
            />
          ))}
        </TileRow>
        {(loans.recent || []).length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>When</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Country</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(loans.recent || []).map((r) => (
                  <tr key={r._id}>
                    <td style={tdStyle}>{fmtDate(r.createdAt)}</td>
                    <td style={tdStyle}>{r.email}</td>
                    <td style={tdStyle}>{r.type}</td>
                    <td style={tdStyle}>{r.amount || '—'}</td>
                    <td style={tdStyle}>{r.country || '—'}</td>
                    <td style={tdStyle}>{r.status || 'pending'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Referrals + dog cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
        <div style={{ ...cardStyle, flex: '1 1 320px' }}>
          <h2 style={sectionTitleStyle}>🤝 Referrals</h2>
          <TileRow>
            <StatTile label="Total referrals" value={fmtNum(referrals.total)} />
          </TileRow>
          {(referrals.top || []).length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr><th style={thStyle}>Referrer</th><th style={thStyle}>Referrals</th></tr>
              </thead>
              <tbody>
                {(referrals.top || []).map((r) => (
                  <tr key={r.email}>
                    <td style={tdStyle}>{r.email}</td>
                    <td style={tdStyle}>{r.referralCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ ...cardStyle, flex: '1 1 320px' }}>
          <h2 style={sectionTitleStyle}>🐕 Dog Cards</h2>
          <TileRow>
            <StatTile label="Saved cards" value={fmtNum(dogCards.savedCards)} />
            <StatTile label="Card users" value={fmtNum(dogCards.users)} />
            <StatTile label="This month" value={fmtNum(dogCards.monthlyGenerations)} />
          </TileRow>
        </div>
      </div>

      <p style={{ color: '#999', fontSize: '0.75rem' }}>
        Generated {new Date(data.generatedAt).toLocaleString()}. Shop and tanks
        purchases are tracked from {`Sept 2026`} onward (earlier ones exist only as emails).
      </p>
    </div>
  );
};

const UsersTab = ({ adminFetch }) => {
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const json = await adminFetch(
          `/api/admin/users?page=${page}&limit=25&q=${encodeURIComponent(search)}`
        );
        if (!cancelled) { setResult(json); setError(''); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminFetch, page, search]);

  const purchases = (u) =>
    ((u.bitWheel && u.bitWheel.totalSpins) || 0) > 0 || ((u.bitFlip && u.bitFlip.totalFlips) || 0) > 0;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ ...sectionTitleStyle, margin: 0, flex: '1 1 auto' }}>
          👥 Users {result ? `(${fmtNum(result.totalUsers)})` : ''}
        </h2>
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(query.trim()); }}
          style={{ display: 'flex', gap: '8px' }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email…"
            style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.85rem' }}
          />
          <button
            type="submit"
            style={{ padding: '8px 14px', backgroundColor: '#00BFFF', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            Search
          </button>
        </form>
      </div>

      {loading && <p style={{ color: '#9dd5ff' }}>Loading users…</p>}
      {error && <p style={{ color: '#fca5a5' }}>Failed to load users: {error}</p>}
      {!loading && result && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Tag</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Signed up</th>
                  <th style={thStyle}>Last login</th>
                  <th style={thStyle}>Logins</th>
                  <th style={thStyle}>Verified</th>
                  <th style={thStyle}>Sub</th>
                  <th style={thStyle}>Played</th>
                </tr>
              </thead>
              <tbody>
                {result.users.map((u) => (
                  <tr key={u.email}>
                    <td style={tdStyle}>{u.email}</td>
                    <td style={tdStyle}>{u.gamerTag || '—'}</td>
                    <td style={tdStyle}>{u.signupSource || '—'}</td>
                    <td style={tdStyle}>{fmtDate(u.signupDate)}</td>
                    <td style={tdStyle}>{fmtDate(u.lastLogin)}</td>
                    <td style={tdStyle}>{u.loginCount || 0}</td>
                    <td style={tdStyle}>{u.isVerified ? '✅' : '—'}</td>
                    <td style={tdStyle}>{u.subscriptionLevel && u.subscriptionLevel !== 'free' ? u.subscriptionLevel : '—'}</td>
                    <td style={tdStyle}>{purchases(u) ? '🎮' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ padding: '6px 12px', border: '1px solid rgba(255, 255, 255, 0.4)', borderRadius: '6px', background: 'transparent', color: '#FFFFFF', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: '0.85rem', color: '#9dd5ff' }}>
              Page {result.page} of {result.pages}
            </span>
            <button
              type="button"
              disabled={page >= result.pages}
              onClick={() => setPage((p) => p + 1)}
              style={{ padding: '6px 12px', border: '1px solid rgba(255, 255, 255, 0.4)', borderRadius: '6px', background: 'transparent', color: '#FFFFFF', cursor: page >= result.pages ? 'default' : 'pointer', opacity: page >= result.pages ? 0.5 : 1 }}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if admin is logged in
    const adminEmail = localStorage.getItem('adminEmail');
    const adminToken = localStorage.getItem('adminToken');

    if (!adminEmail || !adminToken) {
      navigate('/admin/login');
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRole');
    navigate('/admin/login');
  }, [navigate]);

  // Gated fetch helper: sends the admin headers, bounces to login on 401
  const adminFetch = useCallback(async (path) => {
    const response = await fetch(`${baseURL}${path}`, {
      headers: {
        adminEmail: localStorage.getItem('adminEmail') || '',
        adminToken: localStorage.getItem('adminToken') || ''
      }
    });
    if (response.status === 401) {
      handleLogout();
      throw new Error('Session expired');
    }
    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.error || `Request failed (${response.status})`);
    }
    return json;
  }, [handleLogout]);

  if (!isAuthenticated) {
    return <div>Loading...</div>;
  }

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'panoramas', label: 'Panorama Manager', icon: '🖼️' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#1a237e'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1a237e',
        color: 'white',
        padding: '1rem 2rem',
        borderBottom: '2px solid #00BFFF',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h1 style={{
            fontSize: '0.95rem',
            fontWeight: 'bold',
            margin: 0,
            fontFamily: "'Press Start 2P', 'Courier New', monospace",
            letterSpacing: '1px'
          }}>
            🔐 SATOSHI ADVANCE ADMIN
          </h1>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.9rem', opacity: 0.9 }}>
              {localStorage.getItem('adminEmail')}
            </span>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Logout
            </motion.button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{
        backgroundColor: '#283593',
        borderBottom: '1px solid rgba(0, 191, 255, 0.4)',
        padding: '0 2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          gap: '2rem'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '1rem 0',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #00BFFF' : '3px solid transparent',
                color: activeTab === tab.id ? '#00BFFF' : '#9dd5ff',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? '600' : '500',
                fontSize: '1rem',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {activeTab === 'analytics' && <AnalyticsTab adminFetch={adminFetch} />}
        {activeTab === 'users' && <UsersTab adminFetch={adminFetch} />}
        {activeTab === 'panoramas' && <PanoramaAssembler />}
      </div>
    </div>
  );
};

export default AdminPanel;
